/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

      // get the cart notification and cart drawer. They'll be used for the Removed item cart notification
      this.cartNotificationCurrentItems = document.querySelector(
        '[data-cart-notification-current-items]'
      );
      if (this.cartNotificationCurrentItems) {
        this.cartNotification = document.querySelector('cart-notification');
        this.cartDrawer = document.querySelector('cart-drawer');
      }

      this.debouncedOnChange = debounce((event) => {
        if (event.target != document.getElementById('Cart-note')) {
          this.onChange(event);
        }
      }, 300);

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      // check if the Edit icons are enabled. If so, execute the function to listen to any clicks and update the sessionStorage with the info of the Product to be edited
      if (!this.querySelector('[data-cart-edit-link]')) return;
      this.currentProductEdit = undefined;
      this.updateSessionStorageWithEditProductInfo(this.querySelectorAll('[data-cart-edit-link]'));
    }

    onChange(event) {
      // Check if we have an altered way of updating several variants in the cart that are linked
      const lineKeys = event.target.dataset.updateKeys?.split(',') || null;

      this.updateQuantity(
        lineKeys,
        event.target.dataset.index,
        event.target.value,
        document.activeElement.getAttribute('name'),
        event
      );
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-cart-item-contents',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.js-cart-footer-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    /**
     * updateQuantity
     * @description Works together with the Ajax Cart API.
     * @see https://shopify.dev/docs/api/ajax/reference/cart
     * @param {Object} lineKeys
     * @param {Number} line
     * @param {Number} quantity
     * @param {String} name
     * @param {Object} event
     */
    async updateQuantity(lineKeys, line, quantity, name, event) {
      this.enableLoading(line);
      let body;
      let fetchUrl = routes.cart_change_url;

      let sectionsToFetch = [...this.getSectionsToRender().map((section) => section.section)];

      // check if the cart notification current items exists before adding it to the sections to be fetched
      if (this.cartNotificationCurrentItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...this.cartNotification.getSectionsToRender().map((section) => section.id),
        ];
        // return unique values of array (so it removes the duplicates)
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      if (lineKeys.length > 0) {
        fetchUrl = routes.cart_update_url;
        const updateObject = {};
        lineKeys.forEach((item) => {
          updateObject[item] = quantity;
        });

        body = JSON.stringify({
          updates: updateObject,
          sections: sectionsToFetch,
          sections_url: window.location.pathname,
        });
      } else {
        body = JSON.stringify({
          line,
          quantity,
          sections: sectionsToFetch,
          sections_url: window.location.pathname,
        });
      }

      try {
        const response = await fetch(fetchUrl, {
          ...fetchConfig(),
          body,
        });
        const parsedState = await response.json();

        // Error returned in request
        if (response.status === 422 && parsedState.errors) {
          // Read current updated quantity element
          const itemQuantityInput = document.getElementById(`Quantity-${line}`);
          const currentValue = itemQuantityInput.value;
          const maxValue = itemQuantityInput.dataset.max;
          // Reset input value quantity
          if (currentValue > maxValue) itemQuantityInput.value = maxValue;
          // Show the error message to the users
          throw new Error(parsedState.errors);
        } else if (!response.ok) {
          console.log('Error', response.status);
        }

        // Tell the user that something is wrong when there are no sections returned from the call.
        if (!parsedState.sections && event) {
          event.target.value = event.target.defaultValue;
          throw new Error(window.cartStrings.error);
        }

        // update the cart notification current items if there are new items (those might come up after Shopify applies changes like when a Discount is applied)
        if (this.cartNotificationCurrentItems) this.updateCartNotificationItems(parsedState);

        // Loop sections
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        this.toggleEmptyCart();

        // Check if there are errors in the response
        if (parsedState.errors) {
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }
        document.getElementById(`CartItem-${line}`)?.querySelector(`[name="${name}"]`)?.focus();

        // show notification with the option to Undo the removal of an item (only available on the Cart drawer)
        if (this.cartNotificationCurrentItems && quantity == 0) {
          // show the removed product element in the cart notification and show the notification itself with the option to Undo the removal
          const removedItem = this.cartNotification.querySelector(
            `[data-cart-notification-item-keys="${event.target.dataset.updateKeys}"]`
          );
          removedItem?.classList.remove('hidden');

          // clear any existent timeout if other products were removed before the current one
          clearTimeout(this.cartNotification.closeTimeOutOnOpen);

          // open cart notification
          this.cartNotification.open();

          // add event listener to the Undo button of the removed item notification to re-add the removed product
          const undoButtonFromRemovedItem = removedItem?.querySelector(
            '[data-cart-notification-undo]'
          );
          undoButtonFromRemovedItem?.addEventListener('click', this.reAddProductsToCart.bind(this));
        }

        this.disableLoading();

        // re-check if the Edit icons are enabled to execute the function to listen to any clicks and update the sessionStorage with the info of the Product to be edited (needed because the edit elements are lost when re-rendering the cart items)
        if (this.querySelector('[data-cart-edit-link]')) {
          this.updateSessionStorageWithEditProductInfo(
            this.querySelectorAll('[data-cart-edit-link]')
          );
        }
      } catch (error) {
        document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
        document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');
        document.getElementById('cart-errors').textContent = error
          ? error
          : window.cartStrings.error;
        this.disableLoading();
      }
    }

    /**
     * Toggle the empty state of the cart
     */
    toggleEmptyCart() {
      this.currentItemCount = this.querySelectorAll('[data-line-item]').length;
      this.classList.toggle('cart-is-empty', this.currentItemCount === 0);
    }

    // show error texts, should not be deleted.
    updateLiveRegions(line, message) {
      // Check if we can find the line item error element based on line index, if so show the error in line item context
      const lineItemError = document.getElementById(`Line-item-error-${line}`);
      if (lineItemError) {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      }

      // Update attribute from the lineItemStatusElement
      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      // Update the cart live region text with the correct attribute
      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    enableLoading(line) {
      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');
      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    disableLoading() {
      document.querySelector('cart-items').classList.remove('cart-items-is-loading');
    }

    /**
     * updateSessionStorageWithEditProductInfo
     * @description Listen to any clicks on Edit icons and update the sessionStorage with the info of the Product to be edited
     * @param {Object} editLinksArray
     */
    updateSessionStorageWithEditProductInfo(editLinksArray) {
      editLinksArray.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();

          // used on the main-product.js to determine if the browser default dialog should be shown when leaving the page. It checks if a product was being edited before clicking on another one to be edited.
          this.currentProductEdit = sessionStorage.productEdit;

          /**
           * Pass the info of all products to a sessionStorage item.
           * the sessionStorage info is divided in 3 parts:
           * 1 - The current quantity of the edited product which will be used on the PDP
           * 2 - The keys of the products which will be removed from the product-form (the products will be re-added there too)
           * 3 - The upsell IDs which are used to target and pre-select the upsell products on the PDP
           */
          const editProductInfo = `${link.dataset.cartEditQuantity}|||${link.dataset.cartEditKeys}${
            link.dataset.cartEditUpsellIds ? '|||' + link.dataset.cartEditUpsellIds : ''
          }`;

          // add the main sessionStorage item with the Product to be edited or add a new one if there was a previous one set to be edited already
          sessionStorage.productEdit == undefined
            ? (sessionStorage.productEdit = editProductInfo)
            : (sessionStorage.newProductEdit = editProductInfo);

          // update the clearEdit item in the sessionStorage to 'false' so it doesn't clear the Edit mode when the user goes to a PDP
          sessionStorage.clearEdit = 'false';

          // go to the product & variant link
          window.location.href = link.href;
        });
      });
    }

    /**
     * reAddProductsToCart
     * @description Handle the Undo Button click, which will re-add any removed product(s)
     * @param {Object} undoButton
     */
    async reAddProductsToCart(event) {
      // clear the timeout to close the notification after the Undo button is clicked after being opened
      clearTimeout(this.cartNotification.closeTimeOutOnOpen);

      const undoButton = event.currentTarget;
      const removedItem = undoButton.closest('[data-cart-notification-item-keys]');

      // hide the removed product element in the cart notification
      removedItem.classList.add('hidden');

      // check if there's at least one visible removed items left in the cart notification
      const hasRemovedItemLeft = Array.from(
        this.cartNotificationCurrentItems.querySelectorAll('[data-cart-notification-item-keys]')
      ).find((cartItem) => !cartItem.classList.contains('hidden'));

      // let the notification stay visible for 5 seconds or close the notification right away if there are no more removed items left
      hasRemovedItemLeft ? this.cartNotification.close(5000) : this.cartNotification.close();

      // update the undo button to show the loading state
      undoButton.disabled = true;
      undoButton.classList.add('button--loading');

      this.cartItems = document.querySelector('cart-items');

      let sectionsToFetch = [
        ...this.cartNotification.getSectionsToRender().map((section) => section.id),
      ];

      if (this.cartItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...this.cartItems.getSectionsToRender().map((section) => section.section),
        ];
        // return unique values of array (so it removes the duplicates)
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      const mainProductId = +undoButton.dataset.mainProductId;
      let mainProductQty = 1;
      let uniqueSKU = buildUniqueSKU(mainProductId);
      const allItems = [];

      // add all the previously selected Upsell products on Add to Cart
      const upsellIds = undoButton.dataset.upsellIds
        ? undoButton.dataset.upsellIds.split(',')
        : null;
      const upsellTypes = undoButton.dataset.upsellTypes
        ? undoButton.dataset.upsellTypes.split(',')
        : null;

      if (upsellIds) {
        uniqueSKU = buildUniqueSKU(mainProductId, upsellIds);

        upsellIds.forEach((productId, index) => {
          allItems.push({
            id: +productId,
            quantity: mainProductQty,
            properties: {
              _productID: mainProductId,
              _linkedSKU: uniqueSKU,
              ...(upsellTypes[index] != '-' && { _type: upsellTypes[index] }),
            },
          });
        });
      }

      // Get the main item
      allItems.unshift({
        id: mainProductId,
        quantity: mainProductQty,
        properties: {
          _masterSKU: uniqueSKU,
        },
      });

      // gather the array of items to atc and sections to be rerendered
      const body = JSON.stringify({
        items: allItems,
        sections: sectionsToFetch,
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
        const parsedState = await response.json();

        // update the cart drawer
        this.cartDrawer.renderContents(parsedState);

        // re-check if the Edit icons are enabled to execute the function to listen to any clicks and update the sessionStorage with the info of the Product to be edited (needed because the edit elements are lost when re-rendering the cart items)
        if (this.querySelector('[data-cart-edit-link]'))
          this.updateSessionStorageWithEditProductInfo(
            this.querySelectorAll('[data-cart-edit-link]')
          );

        // Set timeout to force animation. Because content is just updated with renderContents
        setTimeout(() => {
          this.cartDrawer.open();
        });
      } catch (error) {
        console.error(error);
      } finally {
        undoButton.classList.remove('button--loading');
        undoButton.removeAttribute('disabled');
      }
    }

    /**
     * Updates the cart notification items when new items are added (e.g., from Shopify discount applications)
     * @param {Object} parsedState - The parsed response from the cart update
     */
    updateCartNotificationItems(parsedState) {
      // current cart notification items
      const currentItemsWrapper = document.getElementById('cart-notification-current-items');
      const currentItemsArray = Array.from(
        currentItemsWrapper?.querySelectorAll('[data-cart-notification-item-keys]')
      );
      const currentItemsKeys = currentItemsArray.map((el) =>
        el.getAttribute('data-cart-notification-item-keys')
      );

      // new cart notification current items
      const newCurrentItemsWrapper = new DOMParser().parseFromString(
        parsedState.sections['cart-notification-current-items'],
        'text/html'
      );
      this.newCurrentItemsArray = Array.from(
        newCurrentItemsWrapper?.querySelectorAll('[data-cart-notification-item-keys]')
      );
      const newCurrentItemsKeys = this.newCurrentItemsArray.map((el) =>
        el.getAttribute('data-cart-notification-item-keys')
      );

      // Filter items that don't exist in the cart notification items yet
      const nonExistingItemKeys = newCurrentItemsKeys.filter(
        (itemKey) => !currentItemsKeys.includes(itemKey)
      );

      // return if all the cart items are already in the cart notification
      if (nonExistingItemKeys.length === 0) return;

      const currentItemsInner =
        currentItemsWrapper?.querySelector('.cart-notification-current-items') ||
        currentItemsWrapper;

      // append the non-existing items to the cart notification
      nonExistingItemKeys.forEach((keyValue) => {
        const newItemElement = this.newCurrentItemsArray.find(
          (item) => item.getAttribute('data-cart-notification-item-keys') === keyValue
        );

        currentItemsInner.appendChild(newItemElement);
      });
    }
  }
  customElements.define('cart-items', CartItems);
}

/* Update the cart note */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'change',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        }, 300)
      );
    }
  }
  customElements.define('cart-note', CartNote);
}
